import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { EmailHistoryModal } from './EmailHistoryModal';

/**
 * One email-history modal for the whole board, the same shape as the notes modal: the ✉️ chip is
 * on cards in several tabs, so the provider owns the modal and the board owner's address, and a
 * card just renders <EmailChip>. Nothing is fetched until a chip is clicked — the modal is only
 * mounted while it is open.
 *
 * The board view mounts it with `key={boardEmail}`, so switching boards drops any open target
 * rather than carrying one board's card into another's modal.
 */
interface EmailTarget {
  opportunityId: string;
  candidateName: string;
}

interface EmailHistoryApi {
  openEmails: (target: EmailTarget) => void;
}

const EmailHistoryCtx = createContext<EmailHistoryApi | null>(null);

/** Null outside a provider — the chip then renders nothing rather than a dead button. */
export function useEmailHistory(): EmailHistoryApi | null {
  return useContext(EmailHistoryCtx);
}

export function EmailHistoryProvider({ boardEmail, children }: { boardEmail: string; children: ReactNode }) {
  const [target, setTarget] = useState<EmailTarget | null>(null);
  const openEmails = useCallback((t: EmailTarget) => setTarget(t), []);
  const close = useCallback(() => setTarget(null), []);
  return (
    <EmailHistoryCtx.Provider value={{ openEmails }}>
      {children}
      {target && (
        <EmailHistoryModal
          boardEmail={boardEmail}
          opportunityId={target.opportunityId}
          candidateName={target.candidateName}
          onClose={close}
        />
      )}
    </EmailHistoryCtx.Provider>
  );
}
